/**
 * The close-disposition vocabularies.
 *
 * Read and write are intentionally DIFFERENT. `duplicate` is a backend-side
 * disposition: an alert can come back carrying it (closed as a duplicate
 * elsewhere in the system) and must render properly, but an operator may never
 * choose it — duplicate is not a v1 write option, and offering it would invite
 * exactly the "close this as a copy of that" flow the contract rules out.
 */
import i18n from "@/shared/lib/i18n/index.ts";
import type {
  CloseDispositionRead,
  CloseDispositionWrite,
} from "@/alerts/types/alerts-workspace";

export interface DispositionDef {
  code: CloseDispositionWrite;
  /** Translation key for the option's label; the picker resolves it. */
  labelKey: "dispositions.falseAlert.label" | "dispositions.insufficientEvidence.label" | "dispositions.duplicate.label" | "dispositions.other.label";
  /** Translation key for the option's explanatory tooltip. */
  helpKey: "dispositions.falseAlert.help" | "dispositions.insufficientEvidence.help" | "dispositions.duplicate.help" | "dispositions.other.help";
}

/** What a Dismiss may submit. `false_alert` is the quick-path default. */
export const CLOSE_DISPOSITIONS: DispositionDef[] = [
  {
    code: "false_alert",
    labelKey: "dispositions.falseAlert.label",
    helpKey: "dispositions.falseAlert.help",
  },
  {
    code: "insufficient_evidence",
    labelKey: "dispositions.insufficientEvidence.label",
    helpKey: "dispositions.insufficientEvidence.help",
  },
  {
    code: "duplicate",
    labelKey: "dispositions.duplicate.label",
    helpKey: "dispositions.duplicate.help",
  },
  {
    code: "other",
    labelKey: "dispositions.other.label",
    helpKey: "dispositions.other.help",
  },
];

export const DEFAULT_CLOSE_DISPOSITION: CloseDispositionWrite = "false_alert";

const DISPOSITION_READ_KEYS: Record<CloseDispositionRead, DispositionDef["labelKey"]> = {
  false_alert: "dispositions.falseAlert.label",
  duplicate: "dispositions.duplicate.label",
  insufficient_evidence: "dispositions.insufficientEvidence.label",
  other: "dispositions.other.label",
};

/** Label an existing disposition; unknown codes render verbatim, never blank. */
export const readDispositionLabel = (code: string | null | undefined): string | null => {
  if (!code) return null;
  const key = DISPOSITION_READ_KEYS[code as CloseDispositionRead];
  return key ? i18n.t(key, { ns: "alerts" }) : code;
};
